import React, { useState, useEffect, useCallback, useRef } from 'react';
import { MessageCircle, RefreshCw, Send, Settings, X, Clock, CheckCheck, Check, AlertCircle, Bot } from 'lucide-react';
import apiService from '../apiService';

/**
 * WhatsApp — boîte de réception du numéro Mbiyo (+243 999 997 244), 2026-10-30.
 * Ce numéro est inscrit sur la Cloud API de Meta : aucune app WhatsApp ne peut le lire. Les messages
 * reçus arrivent par le webhook ; l'équipe les lit et y répond ici. Réponse automatique réglable.
 * Règle Meta : message libre seulement dans les 24 h après le dernier message de la personne.
 * Accès : super-administrateur et rôle support.
 */

const GREEN = '#128C7E';
const fmtTime = (d) => d ? new Date(d).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }) : '';
const fmtDay = (d) => {
  if (!d) return '';
  const x = new Date(d); const today = new Date();
  const y = new Date(); y.setDate(today.getDate() - 1);
  if (x.toDateString() === today.toDateString()) return fmtTime(d);
  if (x.toDateString() === y.toDateString()) return 'Hier';
  return x.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' });
};
const card = { background: 'white', borderRadius: 14, boxShadow: '0 2px 10px rgba(0,0,0,0.05)' };

function StatusTick({ m }) {
  if (m.direction !== 'out') return null;
  if (m.status === 'failed') return <AlertCircle size={13} color="#DC2626" title={m.error || 'Échec'} />;
  if (m.status === 'read') return <CheckCheck size={13} color="#2563EB" />;
  if (m.status === 'delivered') return <CheckCheck size={13} color="#8A919C" />;
  return <Check size={13} color="#8A919C" />;
}

function SettingsModal({ onClose }) {
  const [s, setS] = useState(null);
  const [def, setDef] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  useEffect(() => {
    apiService.get('/admin/whatsapp/settings', { userType: 'admin' })
      .then((r) => { setS(r.settings); setDef(r.default_text || ''); })
      .catch(() => setMsg('Chargement impossible'));
  }, []);
  const save = async () => {
    setBusy(true); setMsg('');
    try { const r = await apiService.put('/admin/whatsapp/settings', s, { userType: 'admin' }); setS(r.settings); setMsg('Enregistré'); }
    catch (e) { setMsg(e?.error || e?.message || 'Erreur'); }
    finally { setBusy(false); }
  };
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(15,18,24,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 }}>
      <div onClick={(e) => e.stopPropagation()} style={{ ...card, width: '100%', maxWidth: 560, padding: '1.25rem 1.5rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
          <h3 style={{ margin: 0, fontSize: '1.05rem' }}>Réponse automatique</h3>
          <button onClick={onClose} style={{ border: 'none', background: 'none', cursor: 'pointer' }}><X size={18} /></button>
        </div>
        {!s ? <p style={{ color: '#5C636E' }}>{msg || 'Chargement…'}</p> : (
          <>
            <label style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12, cursor: 'pointer' }}>
              <input type="checkbox" checked={s.enabled} onChange={(e) => setS({ ...s, enabled: e.target.checked })} />
              <span style={{ fontWeight: 600 }}>Répondre automatiquement aux messages reçus</span>
            </label>
            <p style={{ fontSize: '0.8rem', color: '#5C636E', margin: '0 0 8px' }}>
              Envoyée au plus une fois toutes les 12 h à la même personne, et jamais si l'équipe lui a écrit dans les 12 dernières heures.
            </p>
            <textarea value={s.text} onChange={(e) => setS({ ...s, text: e.target.value })} rows={6} maxLength={1000}
              style={{ width: '100%', boxSizing: 'border-box', border: '1px solid #DCE0E6', borderRadius: 10, padding: 10, fontSize: '0.9rem', fontFamily: 'inherit', resize: 'vertical' }} />
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 10, gap: 8, flexWrap: 'wrap' }}>
              <button onClick={() => setS({ ...s, text: def })} style={{ border: 'none', background: 'none', color: '#5C636E', cursor: 'pointer', fontSize: '0.8rem', textDecoration: 'underline' }}>Texte par défaut</button>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                {msg && <span style={{ fontSize: '0.8rem', color: msg === 'Enregistré' ? '#047857' : '#DC2626' }}>{msg}</span>}
                <button disabled={busy} onClick={save} style={{ background: GREEN, color: 'white', border: 'none', borderRadius: 10, padding: '8px 16px', fontWeight: 700, cursor: 'pointer', opacity: busy ? 0.6 : 1 }}>Enregistrer</button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default function WhatsAppPage() {
  const [convs, setConvs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);       // téléphone sélectionné
  const [th, setTh] = useState(null);               // { messages, window_open, window_closes_at }
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [err, setErr] = useState('');
  const [showSettings, setShowSettings] = useState(false);
  const [query, setQuery] = useState('');
  const endRef = useRef(null);

  const loadConvs = useCallback(async () => {
    try { const r = await apiService.get('/admin/whatsapp/conversations', { userType: 'admin' }); setConvs(r.conversations || []); }
    catch (_) {}
    finally { setLoading(false); }
  }, []);

  const loadThread = useCallback(async (phone) => {
    if (!phone) return;
    try {
      const r = await apiService.get(`/admin/whatsapp/conversations/${encodeURIComponent(phone.replace('+', ''))}`, { userType: 'admin' });
      setTh(r);
      setConvs((cs) => cs.map((c) => (c.phone === phone ? { ...c, unread: 0 } : c)));
    } catch (_) {}
  }, []);

  useEffect(() => { loadConvs(); const id = setInterval(loadConvs, 20000); return () => clearInterval(id); }, [loadConvs]);
  useEffect(() => {
    if (!active) return undefined;
    setTh(null); setErr(''); setText('');
    loadThread(active);
    const id = setInterval(() => loadThread(active), 15000);
    return () => clearInterval(id);
  }, [active, loadThread]);
  useEffect(() => { endRef.current?.scrollIntoView({ block: 'end' }); }, [th?.messages?.length]);

  const send = async () => {
    const body = text.trim();
    if (!body || !active) return;
    setSending(true); setErr('');
    try {
      await apiService.post(`/admin/whatsapp/conversations/${encodeURIComponent(active.replace('+', ''))}/reply`, { text: body }, { userType: 'admin' });
      setText('');
      await loadThread(active); loadConvs();
    } catch (e) {
      setErr(e?.error || e?.message || 'Envoi impossible');
      loadThread(active);
    } finally { setSending(false); }
  };

  const q = query.trim().toLowerCase();
  const shown = q ? convs.filter((c) => (c.name || '').toLowerCase().includes(q) || c.phone.includes(q.replace(/\s/g, ''))) : convs;
  const current = convs.find((c) => c.phone === active);
  const totalUnread = convs.reduce((s, c) => s + (c.unread || 0), 0);

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', marginBottom: '1rem' }}>
        <div>
          <h2 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 10 }}>
            <MessageCircle size={24} color={GREEN} /> WhatsApp
            {totalUnread > 0 && <span style={{ background: GREEN, color: 'white', borderRadius: 999, fontSize: '0.75rem', padding: '2px 9px' }}>{totalUnread}</span>}
          </h2>
          <p style={{ margin: '4px 0 0', color: '#5C636E', fontSize: '0.85rem' }}>
            Messages reçus sur le numéro des codes, +243 999 997 244. Réponse possible dans les 24 h qui suivent le dernier message de la personne.
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={() => setShowSettings(true)} style={{ display: 'flex', alignItems: 'center', gap: 6, border: '1px solid #DCE0E6', background: 'white', borderRadius: 10, padding: '8px 12px', cursor: 'pointer', fontWeight: 600 }}><Settings size={15} /> Réponse automatique</button>
          <button onClick={() => { loadConvs(); if (active) loadThread(active); }} style={{ display: 'flex', alignItems: 'center', gap: 6, border: '1px solid #DCE0E6', background: 'white', borderRadius: 10, padding: '8px 12px', cursor: 'pointer' }}><RefreshCw size={15} /></button>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(260px, 340px) 1fr', gap: 16, minHeight: 560 }}>
        {/* Conversations */}
        <div style={{ ...card, overflow: 'hidden', display: 'flex', flexDirection: 'column', maxHeight: '72vh' }}>
          <div style={{ padding: 10, borderBottom: '1px solid #EEF0F3' }}>
            <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Rechercher un nom ou un numéro"
              style={{ width: '100%', boxSizing: 'border-box', border: '1px solid #DCE0E6', borderRadius: 10, padding: '8px 10px', fontSize: '0.85rem' }} />
          </div>
          <div style={{ overflowY: 'auto', flex: 1 }}>
            {loading ? <p style={{ padding: 16, color: '#5C636E' }}>Chargement…</p>
              : shown.length === 0 ? (
                <div style={{ padding: 20, color: '#5C636E', fontSize: '0.85rem', lineHeight: 1.5 }}>
                  Aucun message pour l'instant. Les messages arrivent dès que l'application Meta est « En ligne » et que le champ « messages » du webhook est abonné.
                </div>
              ) : shown.map((c) => (
                <button key={c.phone} onClick={() => setActive(c.phone)}
                  style={{ display: 'flex', width: '100%', textAlign: 'left', gap: 10, padding: '10px 12px', border: 'none', borderBottom: '1px solid #F3F4F6', background: active === c.phone ? '#E9F5F2' : 'white', cursor: 'pointer' }}>
                  <div style={{ width: 38, height: 38, borderRadius: 999, background: '#DCF2EC', color: GREEN, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, flexShrink: 0 }}>
                    {(c.name || '#').trim().charAt(0).toUpperCase()}
                  </div>
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 6 }}>
                      <span style={{ fontWeight: c.unread ? 800 : 600, fontSize: '0.9rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.name || c.phone}</span>
                      <span style={{ fontSize: '0.72rem', color: c.unread ? GREEN : '#8A919C', flexShrink: 0 }}>{fmtDay(c.last_at)}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 6, marginTop: 2 }}>
                      <span style={{ fontSize: '0.8rem', color: '#5C636E', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {c.last_direction === 'out' ? 'Vous : ' : ''}{c.last_text}
                      </span>
                      {c.unread > 0 && <span style={{ background: GREEN, color: 'white', borderRadius: 999, fontSize: '0.7rem', padding: '1px 7px', flexShrink: 0 }}>{c.unread}</span>}
                    </div>
                  </div>
                </button>
              ))}
          </div>
        </div>

        {/* Fil */}
        <div style={{ ...card, display: 'flex', flexDirection: 'column', overflow: 'hidden', maxHeight: '72vh', minWidth: 0 }}>
          {!active ? (
            <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#8A919C', padding: 24, textAlign: 'center' }}>Choisissez une conversation.</div>
          ) : (
            <>
              <div style={{ padding: '12px 16px', borderBottom: '1px solid #EEF0F3' }}>
                <div style={{ fontWeight: 700 }}>{current?.name || active}</div>
                <div style={{ fontSize: '0.8rem', color: '#5C636E', display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                  <span>{active}</span>
                  {current?.user_id ? <span style={{ color: '#047857' }}>Compte client Mbiyo · {current.user_id}</span> : <span>Aucun compte client à ce numéro</span>}
                  {current?.profile_name && current.profile_name !== current.name && <span>WhatsApp : {current.profile_name}</span>}
                </div>
              </div>
              <div style={{ flex: 1, overflowY: 'auto', padding: 16, background: '#F4F1EC', display: 'flex', flexDirection: 'column', gap: 6 }}>
                {!th ? <p style={{ color: '#5C636E' }}>Chargement…</p> : th.messages.map((m) => (
                  <div key={m._id} style={{ alignSelf: m.direction === 'out' ? 'flex-end' : 'flex-start', maxWidth: '75%' }}>
                    <div style={{ background: m.direction === 'out' ? '#D9FDD3' : 'white', borderRadius: 10, padding: '7px 10px', boxShadow: '0 1px 1px rgba(0,0,0,0.06)', fontSize: '0.9rem', whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontStyle: m.kind !== 'text' && m.kind !== 'button' && m.kind !== 'interactive' ? 'italic' : 'normal' }}>
                      {m.auto && <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: '0.7rem', color: GREEN, fontWeight: 700, marginBottom: 2 }}><Bot size={12} /> Réponse automatique</div>}
                      {m.text}
                      <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 4, fontSize: '0.68rem', color: '#8A919C', marginTop: 2 }}>
                        {m.direction === 'out' && m.sent_by && !m.auto && <span>{m.sent_by} ·</span>}
                        <span>{new Date(m.created_at).toLocaleString('fr-FR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</span>
                        <StatusTick m={m} />
                      </div>
                    </div>
                    {m.status === 'failed' && m.error && <div style={{ fontSize: '0.7rem', color: '#DC2626', textAlign: 'right', marginTop: 2 }}>{m.error}</div>}
                  </div>
                ))}
                <div ref={endRef} />
              </div>
              <div style={{ borderTop: '1px solid #EEF0F3', padding: 10 }}>
                {th && !th.window_open ? (
                  <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', color: '#92400E', background: '#FEF3C7', borderRadius: 10, padding: '10px 12px', fontSize: '0.82rem' }}>
                    <Clock size={16} style={{ flexShrink: 0, marginTop: 1 }} />
                    <span>La fenêtre de 24 h est fermée. WhatsApp n'autorise plus de message libre tant que cette personne n'a pas réécrit. Contactez-la par l'app, par e-mail ou par téléphone.</span>
                  </div>
                ) : (
                  <>
                    {th?.window_closes_at && <div style={{ fontSize: '0.72rem', color: '#8A919C', marginBottom: 6 }}>Réponse possible jusqu'à {new Date(th.window_closes_at).toLocaleString('fr-FR', { weekday: 'short', hour: '2-digit', minute: '2-digit' })}</div>}
                    <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
                      <textarea value={text} onChange={(e) => setText(e.target.value)} rows={2} maxLength={4000} placeholder="Écrire une réponse…"
                        onKeyDown={(e) => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) send(); }}
                        style={{ flex: 1, border: '1px solid #DCE0E6', borderRadius: 10, padding: '8px 10px', fontSize: '0.9rem', fontFamily: 'inherit', resize: 'vertical' }} />
                      <button disabled={sending || !text.trim()} onClick={send}
                        style={{ background: GREEN, color: 'white', border: 'none', borderRadius: 10, padding: '10px 14px', cursor: 'pointer', opacity: sending || !text.trim() ? 0.5 : 1, display: 'flex', alignItems: 'center', gap: 6, fontWeight: 700 }}>
                        <Send size={15} /> Envoyer
                      </button>
                    </div>
                    {err && <div style={{ color: '#DC2626', fontSize: '0.8rem', marginTop: 6 }}>{err}</div>}
                  </>
                )}
              </div>
            </>
          )}
        </div>
      </div>
      {showSettings && <SettingsModal onClose={() => setShowSettings(false)} />}
    </div>
  );
}
